import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import './LeadDossierModal.css';

/**
 * LeadDossierModal
 * Interactive Slide-out Drawer / Modal displaying rich executive background,
 * company intelligence, outreach icebreakers, and verified web citations
 * powered by Real-Time Web Search and AI synthesis.
 */
export default function LeadDossierModal({
  isOpen,
  onClose,
  lead,
  dossier,
  loading,
  error,
  onRefresh,
  portalTarget,
}) {
  const [activeTab, setActiveTab] = useState('executive'); // 'executive' | 'company' | 'icebreakers' | 'sources'
  const [copiedHookIdx, setCopiedHookIdx] = useState(null);
  const [copiedFullDossier, setCopiedFullDossier] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [coLogoError, setCoLogoError] = useState(false);

  React.useEffect(() => {
    if (loading) {
      setActiveTab('executive');
      setImgError(false);
      setCoLogoError(false);
    }
  }, [loading]);

  if (!isOpen) return null;

  const personName = lead?.personName || lead?.name || 'Lead';
  const jobTitle = lead?.jobTitle || lead?.title || '';
  const companyName = lead?.companyName || lead?.company || '';
  const location = lead?.location || '';
  const domain = lead?.domain || '';
  const photoUrl = lead?.photoUrl || null;
  const companyLogo =
    lead?.companyLogo ||
    (domain
      ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=64`
      : null);

  const companyTag =
    lead?.industry ||
    dossier?.companyIntelligence?.industry ||
    dossier?.companyIntelligence?.targetMarket ||
    (dossier?.keyTopics && dossier.keyTopics[0]) ||
    (domain ? domain : null);

  const handleCopyHook = (text, idx) => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
      setCopiedHookIdx(idx);
      setTimeout(() => setCopiedHookIdx(null), 2000);
    }
  };

  const handleCopyFullDossier = () => {
    if (!dossier) return;
    const lines = [
      `LEAD INTELLIGENCE DOSSIER: ${personName} (${jobTitle} at ${companyName})`,
      `Location: ${location} | Domain: ${domain}`,
      '',
      '--- EXECUTIVE SUMMARY ---',
      dossier.executiveSummary || 'No summary available.',
      '',
      '--- CAREER HIGHLIGHTS ---',
      ...(dossier.careerHighlights || []).map((h) => `• ${h}`),
      '',
      '--- COMPANY INTELLIGENCE ---',
      `Core Business: ${dossier.companyIntelligence?.coreBusiness || 'N/A'}`,
      `Recent Developments: ${dossier.companyIntelligence?.recentDevelopments || 'N/A'}`,
      `Target Market: ${dossier.companyIntelligence?.targetMarket || 'N/A'}`,
      '',
      '--- OUTREACH ICEBREAKERS ---',
      ...(dossier.icebreakers || []).map((ib) => `[${ib.angle}] ${ib.text}`),
      '',
      '--- VERIFIED SOURCES ---',
      ...(dossier.sources || []).map((s) => `• ${s.title}: ${s.url}`),
      '',
      `Generated via Real-Time Web Intelligence at ${dossier.provenance?.searchedAt || new Date().toISOString()}`,
    ];

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(lines.join('\n'));
      setCopiedFullDossier(true);
      setTimeout(() => setCopiedFullDossier(false), 2000);
    }
  };

  const modalContent = (
    <div
      className="lead-dossier-overlay"
      onClick={onClose}
      data-testid="lead-dossier-overlay"
      style={{ zIndex: 2147483647 }}
    >
      <div
        className="lead-dossier-window lead-dossier-drawer"
        onClick={(e) => e.stopPropagation()}
        data-testid="lead-dossier-drawer"
      >
        {/* ── Top Header ── */}
        <div className="lead-dossier-header">
          <div className="lead-dossier-lead-info">
            {/* Box 1: User Photo (if available) */}
            <div className="lead-dossier-avatar">
              <img
                src={
                  !imgError && photoUrl
                    ? photoUrl
                    : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                        personName || companyName
                      )}&size=160&background=e0e7ff&color=4338ca&bold=true`
                }
                alt={personName}
                onError={() => setImgError(true)}
              />
            </div>

            {/* Box 2: Middle Information Column */}
            <div className="lead-dossier-identity">
              {/* Row 1: Name */}
              <h2 className="lead-dossier-name">{personName}</h2>

              {/* Row 2: Position + Company beside each other */}
              <div className="lead-dossier-position-row">
                {jobTitle && <span className="lead-dossier-position">{jobTitle}</span>}
                {jobTitle && companyName && <span className="lead-dossier-dot">&bull;</span>}
                {companyName && (
                  <div className="lead-dossier-company-badge">
                    {companyLogo && !coLogoError ? (
                      <img
                        src={companyLogo}
                        alt={companyName}
                        className="lead-dossier-co-logo-img"
                        onError={() => setCoLogoError(true)}
                      />
                    ) : (
                      <i className="fa-solid fa-building lead-dossier-co-icon text-slate-400"></i>
                    )}
                    <span className="lead-dossier-company-name">{companyName}</span>
                  </div>
                )}
                {location && (
                  <>
                    <span className="lead-dossier-dot">&bull;</span>
                    <span className="lead-dossier-location">
                      <i className="fa-solid fa-location-dot text-slate-400"></i>
                      <span>{location}</span>
                    </span>
                  </>
                )}
              </div>

              {/* Row 3: Confident rate & status badges */}
              <div className="lead-dossier-tags-row">
                {!loading && dossier?.confidenceScore && (
                  <span className="lead-dossier-confidence-chip">
                    <i className="fa-solid fa-shield-check"></i>
                    <span>{Math.round(dossier.confidenceScore * 100)}% Confidence</span>
                  </span>
                )}
                {!loading && dossier?.fromCache && (
                  <span
                    className="lead-dossier-cache-chip"
                    title="Instant retrieval from verified cache"
                  >
                    <i className="fa-solid fa-bolt"></i> Cached
                  </span>
                )}
                <span className="lead-dossier-ai-badge">
                  <i className="fa-solid fa-wand-magic-sparkles"></i>
                  <span>Live Web Intel</span>
                </span>
              </div>
            </div>
          </div>

          <div className="lead-dossier-header-actions">
            {dossier && !loading && (
              <>
                <button
                  type="button"
                  className="lead-dossier-action-btn"
                  onClick={handleCopyFullDossier}
                  title="Copy complete dossier to clipboard"
                >
                  <i
                    className={`fa-solid ${copiedFullDossier ? 'fa-check text-emerald-600' : 'fa-copy'}`}
                  ></i>
                  <span>{copiedFullDossier ? 'Copied' : 'Copy Dossier'}</span>
                </button>
                <button
                  type="button"
                  className="lead-dossier-action-btn"
                  onClick={onRefresh}
                  title="Re-scan and refresh web intelligence"
                >
                  <i className="fa-solid fa-rotate"></i>
                  <span>Re-scan</span>
                </button>
              </>
            )}
            <button
              type="button"
              className="lead-dossier-close-btn"
              onClick={onClose}
              title="Close dossier"
              aria-label="Close"
            >
              &times;
            </button>
          </div>
        </div>

        {/* ── Sub-Nav Tabs (only shown after all research resources are ready) ── */}
        {!loading && dossier && !error && (
          <div className="lead-dossier-tabs-bar" data-testid="lead-dossier-tabs-bar">
            <button
              type="button"
              className={`lead-dossier-tab-btn ${activeTab === 'executive' ? 'active' : ''}`}
              onClick={() => setActiveTab('executive')}
            >
              <i className="fa-solid fa-user-tie"></i>
              <span>Executive Bio</span>
            </button>
            <button
              type="button"
              className={`lead-dossier-tab-btn ${activeTab === 'company' ? 'active' : ''}`}
              onClick={() => setActiveTab('company')}
            >
              <i className="fa-solid fa-building"></i>
              <span>Company Signals</span>
            </button>
            <button
              type="button"
              className={`lead-dossier-tab-btn ${activeTab === 'icebreakers' ? 'active' : ''}`}
              onClick={() => setActiveTab('icebreakers')}
            >
              <i className="fa-solid fa-comment-dots"></i>
              <span>Outreach Icebreakers</span>
              {dossier?.icebreakers?.length > 0 && (
                <span className="lead-dossier-tab-count">{dossier.icebreakers.length}</span>
              )}
            </button>
            <button
              type="button"
              className={`lead-dossier-tab-btn ${activeTab === 'sources' ? 'active' : ''}`}
              onClick={() => setActiveTab('sources')}
            >
              <i className="fa-solid fa-globe"></i>
              <span>Web Sources</span>
              {dossier?.sources?.length > 0 && (
                <span className="lead-dossier-tab-count">{dossier.sources.length}</span>
              )}
            </button>
          </div>
        )}

        {/* ── Drawer Body ── */}
        <div className="lead-dossier-body">
          {loading ? (
            <div className="lead-dossier-loading-box" data-testid="lead-dossier-loading">
              <div className="lead-dossier-spinner"></div>
              <h4 className="lead-dossier-loading-title">Conducting Deep Web Research</h4>
              <p className="lead-dossier-loading-desc">
                Scanning real-time online sources and synthesizing an executive background
                dossier...
              </p>
              <div className="lead-dossier-loading-steps">
                <div className="lead-dossier-step-item active">
                  <i className="fa-solid fa-check"></i>
                  <span>
                    Targeted web indexing for {personName} ({companyName})
                  </span>
                </div>
                <div className="lead-dossier-step-item active">
                  <i className="fa-solid fa-circle-notch fa-spin"></i>
                  <span>Retrieving executive profiles, press releases &amp; news</span>
                </div>
                <div className="lead-dossier-step-item">
                  <i className="fa-regular fa-circle"></i>
                  <span>Synthesizing outreach icebreakers &amp; company signals</span>
                </div>
              </div>
            </div>
          ) : error ? (
            <div className="lead-dossier-error-box" data-testid="lead-dossier-error">
              <div className="lead-dossier-error-icon">
                <i className="fa-solid fa-triangle-exclamation"></i>
              </div>
              <h4 className="lead-dossier-error-title">Research Temporarily Unavailable</h4>
              <p className="lead-dossier-error-desc">{error}</p>
              <button type="button" className="lead-dossier-retry-btn" onClick={onRefresh}>
                <i className="fa-solid fa-rotate-right mr-1.5"></i> Retry Research
              </button>
            </div>
          ) : dossier ? (
            <div className="lead-dossier-content" data-testid="lead-dossier-content">
              {/* TAB 1: EXECUTIVE BIO */}
              {activeTab === 'executive' && (
                <div className="lead-dossier-section animate-fade-in">
                  <div className="lead-dossier-card primary">
                    <div className="lead-dossier-card-head">
                      <div className="lead-dossier-card-icon">
                        <i className="fa-solid fa-user-tie"></i>
                      </div>
                      <h3 className="lead-dossier-card-title">Executive Summary</h3>
                    </div>
                    <p className="lead-dossier-summary-text">{dossier.executiveSummary}</p>
                  </div>

                  {dossier.careerHighlights && dossier.careerHighlights.length > 0 && (
                    <div className="lead-dossier-card">
                      <div className="lead-dossier-card-head">
                        <div className="lead-dossier-card-icon">
                          <i className="fa-solid fa-award"></i>
                        </div>
                        <h3 className="lead-dossier-card-title">
                          Career Highlights &amp; Background
                        </h3>
                      </div>
                      <ul className="lead-dossier-highlights-list">
                        {dossier.careerHighlights.map((hl, idx) => (
                          <li key={idx} className="lead-dossier-highlight-item">
                            <i className="fa-solid fa-circle-check lead-dossier-highlight-icon"></i>
                            <span className="lead-dossier-highlight-text">{hl}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {dossier.keyTopics && dossier.keyTopics.length > 0 && (
                    <div className="lead-dossier-card">
                      <div className="lead-dossier-card-head">
                        <div className="lead-dossier-card-icon">
                          <i className="fa-solid fa-tags"></i>
                        </div>
                        <h3 className="lead-dossier-card-title">
                          Domain Expertise &amp; Core Topics
                        </h3>
                      </div>
                      <div className="lead-dossier-tags-wrap">
                        {dossier.keyTopics.map((topic, idx) => (
                          <span key={idx} className="lead-dossier-tag">
                            #{topic}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* TAB 2: COMPANY SIGNALS */}
              {activeTab === 'company' && (
                <div className="lead-dossier-section animate-fade-in">
                  <div className="lead-dossier-card">
                    <div className="lead-dossier-card-head">
                      <div className="lead-dossier-card-icon">
                        <i className="fa-solid fa-bullseye"></i>
                      </div>
                      <h3 className="lead-dossier-card-title">Core Business &amp; Offering</h3>
                    </div>
                    <p className="lead-dossier-text">
                      {dossier.companyIntelligence?.coreBusiness ||
                        `${companyName} operates in the commercial market providing products and domain services.`}
                    </p>
                  </div>

                  <div className="lead-dossier-card">
                    <div className="lead-dossier-card-head">
                      <div className="lead-dossier-card-icon">
                        <i className="fa-solid fa-newspaper"></i>
                      </div>
                      <h3 className="lead-dossier-card-title">
                        Recent Developments &amp; Market Presence
                      </h3>
                    </div>
                    <p className="lead-dossier-text">
                      {dossier.companyIntelligence?.recentDevelopments ||
                        'Active business footprint identified through public web releases and industry listings.'}
                    </p>
                  </div>

                  <div className="lead-dossier-card">
                    <div className="lead-dossier-card-head">
                      <div className="lead-dossier-card-icon">
                        <i className="fa-solid fa-users-viewfinder"></i>
                      </div>
                      <h3 className="lead-dossier-card-title">Target Market</h3>
                    </div>
                    <p className="lead-dossier-text">
                      {dossier.companyIntelligence?.targetMarket ||
                        'Enterprise, B2B, and commercial mid-market client segment.'}
                    </p>
                  </div>
                </div>
              )}

              {/* TAB 3: OUTREACH ICEBREAKERS */}
              {activeTab === 'icebreakers' && (
                <div className="lead-dossier-section animate-fade-in">
                  <div className="lead-dossier-intro-banner">
                    <i className="fa-solid fa-lightbulb lead-dossier-intro-icon"></i>
                    <div>
                      <strong>SDR Outreach Advice: </strong>
                      <span>
                        Use these tailored conversation starters for cold emails, InMail messages,
                        or discovery call openers.
                      </span>
                    </div>
                  </div>

                  <div className="lead-dossier-icebreakers-grid">
                    {dossier.icebreakers && dossier.icebreakers.length > 0 ? (
                      dossier.icebreakers.map((ib, idx) => (
                        <div key={idx} className="lead-dossier-icebreaker-card">
                          <div className="lead-dossier-ib-header">
                            <span className="lead-dossier-ib-angle">
                              <i className="fa-solid fa-sparkles"></i>
                              <span>{ib.angle || `Hook #${idx + 1}`}</span>
                            </span>
                            <button
                              type="button"
                              className="lead-dossier-ib-copy-btn"
                              onClick={() => handleCopyHook(ib.text, idx)}
                              title="Copy icebreaker text"
                            >
                              <i
                                className={`fa-solid ${
                                  copiedHookIdx === idx ? 'fa-check text-emerald-600' : 'fa-copy'
                                }`}
                              ></i>
                              <span>{copiedHookIdx === idx ? 'Copied' : 'Copy Hook'}</span>
                            </button>
                          </div>
                          <p className="lead-dossier-ib-text">&ldquo;{ib.text}&rdquo;</p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-slate-500 p-4">No icebreakers generated.</p>
                    )}
                  </div>
                </div>
              )}

              {/* TAB 4: WEB SOURCES & REFERENCES */}
              {activeTab === 'sources' && (
                <div className="lead-dossier-section animate-fade-in">
                  <div className="lead-dossier-references-header">
                    <span className="lead-dossier-references-hint">
                      Verified public sources referenced to compile this executive dossier.
                    </span>
                  </div>

                  <div className="lead-dossier-sources-list">
                    {dossier.sources && dossier.sources.length > 0 ? (
                      dossier.sources.map((src, idx) => (
                        <div key={idx} className="lead-dossier-source-card lead-dossier-ref-item">
                          <div className="lead-dossier-ref-index">{idx + 1}</div>
                          <div className="lead-dossier-ref-info">
                            <div className="lead-dossier-source-top">
                              <a
                                href={src.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="lead-dossier-source-title"
                                title="Open reference source in new tab"
                              >
                                <span>{src.title || src.domain || 'Online Reference'}</span>
                                <i className="fa-solid fa-arrow-up-right-from-square"></i>
                              </a>
                              {src.score && (
                                <span className="lead-dossier-source-score">
                                  {src.score}% Relevance
                                </span>
                              )}
                            </div>
                            <div className="lead-dossier-ref-meta">
                              <span className="lead-dossier-source-domain">
                                <i className="fa-solid fa-globe mr-1 text-slate-400"></i>
                                {src.domain}
                              </span>
                              <span className="lead-dossier-ref-url" title={src.url}>
                                {src.url}
                              </span>
                            </div>
                          </div>
                          <a
                            href={src.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="lead-dossier-ref-visit-btn"
                            title="Visit source"
                          >
                            <span>Visit</span>
                            <i className="fa-solid fa-arrow-up-right-from-square text-xs"></i>
                          </a>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-slate-500 p-4">No live web sources available.</p>
                    )}
                  </div>

                  {dossier.provenance && (
                    <div className="lead-dossier-provenance-bar">
                      <i className="fa-solid fa-fingerprint text-slate-400"></i>
                      <span>
                        Verified live at{' '}
                        {new Date(dossier.provenance.searchedAt).toLocaleTimeString()} (
                        {dossier.provenance.sourcesCount} sources referenced)
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* ── Drawer Footer ── */}
        <div className="lead-dossier-footer">
          <div className="lead-dossier-footer-hint">
            <i className="fa-solid fa-circle-info text-slate-400"></i>
            <span>Lead Huntrix Discovery Engine</span>
          </div>
          <button type="button" className="lead-dossier-btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );

  const mountNode =
    portalTarget !== undefined
      ? portalTarget
      : typeof document !== 'undefined'
        ? document.body
        : null;

  if (mountNode) {
    return ReactDOM.createPortal(modalContent, mountNode);
  }

  return modalContent;
}
